import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Shared/Sidebar';
import Header from '../Shared/Header';
import { API_BASE_URL } from '../../config';
import { useTranslation } from 'react-i18next';
import './ExamBooster.css';

const ExamBoosterGenerator = () => {
    const { t } = useTranslation(['exam_booster', 'common', 'navigation']);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        exam: '',
        numQuestions: 10,
        difficulty: 'Mix',
        language: 'English'
    });

    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState({
        generating: false,
        credits: false,
        topup: false
    });
    const [error, setError] = useState(null);
    const [credits, setCredits] = useState({ total: 0, remaining: 0 });
    const [showBuyModal, setShowBuyModal] = useState(false);
    const [cashfree, setCashfree] = useState(null);

    const userId = localStorage.getItem('userId');

    // Initialize Cashfree
    useEffect(() => {
        if (window.Cashfree) {
            setCashfree(window.Cashfree({ mode: "production" }));
        }
    }, []);

    // Fetch credits
    const fetchCredits = useCallback(async () => {
        setLoading(prev => ({ ...prev, credits: true }));
        try {
            const res = await fetch(`${API_BASE_URL}get_usage_count.php?user_id=${userId}&type=exam_booster`);
            const data = await res.json();
            if (data.status === 'success') {
                setCredits({ total: data.total_count, remaining: data.remaining_count });
            }
        } catch (err) {
            console.error("Error fetching credits:", err);
        } finally {
            setLoading(prev => ({ ...prev, credits: false }));
        }
    }, [userId]);

    useEffect(() => {
        const fetchExams = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}get_exam_boosters.php`);
                const data = await res.json();
                setExams(Array.isArray(data.exams) ? data.exams : []);
            } catch (err) {
                console.error("Error fetching exams:", err);
                setExams([]);
            }
        };

        fetchExams();
        fetchCredits();
    }, [userId, fetchCredits]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const verifyPayment = async (orderId) => {
        try {
            const res = await fetch(`${API_BASE_URL}verify_cashfree_payment.php?order_id=${orderId}`);
            const data = await res.json();
            if (data.status === 'success' && data.order_status === 'SUCCESS') {
                alert(t('exam_booster:exam_booster.alerts.paymentFailed', { status: data.order_status || "FAILED", defaultValue: "Payment not successful." }));
            }
        } catch (err) {
            console.error("Verification error:", err);
        }
    };

    const handleTopUp = async () => {
        if (!cashfree) {
            alert(t('exam_booster:exam_booster.alerts.gatewayError', 'Payment gateway not initialized. Please refresh.'));
            return;
        }

        setLoading(prev => ({ ...prev, topup: true }));
        try {
            const res = await fetch(`${API_BASE_URL}create_cashfree_order.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userId,
                    amount: 19,
                    plan_name: 'Exam Booster Package'
                })
            });
            const data = await res.json();

            if (data.status === 'success' && data.payment_session_id) {
                let checkoutOptions = {
                    paymentSessionId: data.payment_session_id,
                    redirectTarget: "_modal",
                };

                cashfree.checkout(checkoutOptions).then(() => {
                    verifyPayment(data.order_id);
                });
            } else {
                alert(data.message || t('exam_booster:exam_booster.alerts.connError', 'Connection error. Please check your internet.'));
            }
        } catch (err) {
            console.error("Top-up error:", err);
            alert(t('exam_booster:exam_booster.alerts.connError', 'Connection error. Please check your internet.'));
        } finally {
            setLoading(prev => ({ ...prev, topup: false }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!formData.exam) {
            setError(t('exam_booster:exam_booster.alerts.selectExam', "Please select an exam first."));
            return;
        }

        if (credits.remaining <= 0) {
            setShowBuyModal(true);
            return;
        }

        setLoading(prev => ({ ...prev, generating: true }));

        try {
            const response = await fetch(`${API_BASE_URL}generate_exam_booster.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    userId,
                    exam_name: formData.exam,
                    count: formData.numQuestions,
                    level: formData.difficulty,
                    language: formData.language
                })
            });

            const result = await response.json();

            if (result.status === 'success') {
                sessionStorage.setItem('booster_questions', JSON.stringify(result.questions));
                sessionStorage.setItem('booster_exam', formData.exam);
                fetchCredits();
                navigate('/exam-booster/engine');
            } else if (result.insufficient_credits) {
                setShowBuyModal(true);
            } else {
                setError(result.message || t('exam_booster:exam_booster.alerts.generationFailed', "Failed to generate questions. Please try again."));
            }
        } catch (err) {
            console.error("Error:", err);
            setError(t('exam_booster:exam_booster.alerts.connError', "Connection error. Please check your internet."));
        } finally {
            setLoading(prev => ({ ...prev, generating: false }));
        }
    };

    return (
        <div className="dashboard-wrapper">
            <div className="dashboard-container">
                <Sidebar active="exam_booster" />
                <main className="main-content-area">
                    <Header showActions={true} hidePill={true} />

                    {loading.generating && (
                        <div className="eb-ai-loader-overlay">
                            <div className="eb-ai-loader-content">
                                <div className="eb-ai-loader-visual">
                                    <div className="eb-loader-ring eb-loader-ring-outer"></div>
                                    <div className="eb-loader-ring eb-loader-ring-inner"></div>
                                    <div className="eb-loader-core">
                                        <img src="/ai_brain_loader.png" alt="AI Brain" className="eb-ai-brain-img" />
                                    </div>
                                </div>
                                <div className="eb-ai-loader-text">
                                    <h2 className="eb-gradient-text" style={{ fontSize: '2rem', marginBottom: '10px' }}>{t('exam_booster:exam_booster.thinking', 'SkillTherapy AI is Thinking...')}</h2>
                                    <p style={{ color: '#94a3b8', fontSize: '1.1rem' }}>{t('exam_booster:exam_booster.generatingStep', 'Generating specialized questions with detailed expert insights...')}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="dashboard-scroll-container">
                        <div className="eb-generator-content-wrapper" style={{ padding: '30px' }}>
                            <div className="eb-container">
                                <div className="eb-generator-section">
                                    <div className="eb-hero-title">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            <h2>{t('exam_booster:exam_booster.title', 'SkillTherapy AI Exam Ready Practice')}</h2>
                                        </div>
                                        <div className="eb-credits-badge">
                                            <span className="eb-icon">⚡</span>
                                            <span className="eb-count">
                                                {loading.credits ? '...' : `${credits.remaining} / ${credits.total}`} {t('exam_booster:exam_booster.credits', 'Credits')}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="eb-hero-subtext">{t('exam_booster:exam_booster.subtext', 'Generate specialized AI-powered MCQ practice sets tailored to your target exam.')}</p>

                                    {error && <div className="eb-error-message" style={{ color: '#ff4444', marginBottom: '20px', padding: '10px', background: 'rgba(255,68,68,0.1)', borderRadius: '8px' }}>{error}</div>}

                                    <form className="eb-form-grid" onSubmit={handleSubmit}>
                                        <div className="eb-form-group">
                                            <label>{t('exam_booster:exam_booster.form.targetExam', 'Target Exam')}</label>
                                            <select name="exam" value={formData.exam} onChange={handleChange} required>
                                                <option value="">{t('exam_booster:exam_booster.form.selectExam', 'Select your exam')}</option>
                                                {exams.map((exam, idx) => (
                                                    <option key={idx} value={exam.exam_name}>{exam.exam_name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="eb-form-group">
                                            <label>{t('exam_booster:exam_booster.form.numQuestions', 'Number of Questions')}</label>
                                            <select name="numQuestions" value={formData.numQuestions} onChange={handleChange}>
                                                <option value="10">{t('exam_booster:exam_booster.form.questionsCount', { count: 10, defaultValue: '10 Questions' })}</option>
                                                <option value="20">{t('exam_booster:exam_booster.form.questionsCount', { count: 20, defaultValue: '20 Questions' })}</option>
                                                <option value="30">{t('exam_booster:exam_booster.form.questionsCount', { count: 30, defaultValue: '30 Questions' })}</option>
                                            </select>
                                        </div>

                                        <div className="eb-form-group">
                                            <label>{t('exam_booster:exam_booster.form.difficultyLevel', 'Difficulty Level')}</label>
                                            <select name="difficulty" value={formData.difficulty} onChange={handleChange}>
                                                <option value="Mix">{t('exam_booster:exam_booster.form.difficulties.mix', 'Mix question')}</option>
                                                <option value="Easy">{t('exam_booster:exam_booster.form.difficulties.easy', 'Easy')}</option>
                                                <option value="Medium">{t('exam_booster:exam_booster.form.difficulties.medium', 'Medium')}</option>
                                                <option value="Hard">{t('exam_booster:exam_booster.form.difficulties.hard', 'Hard')}</option>
                                                <option value="Advanced">{t('exam_booster:exam_booster.form.difficulties.advanced', 'Advanced')}</option>
                                            </select>
                                        </div>

                                        <div className="eb-form-group">
                                            <label>{t('exam_booster:exam_booster.form.language', 'Language')}</label>
                                            <select name="language" value={formData.language} onChange={handleChange}>
                                                <option value="English">{t('common:languages.english', 'English')}</option>
                                                <option value="Hindi">{t('common:languages.hindi', 'Hindi')}</option>
                                                <option value="Bengali">{t('common:languages.bengali', 'Bengali')}</option>
                                                <option value="Marathi">{t('common:languages.marathi', 'Marathi')}</option>
                                                <option value="Gujarati">{t('common:languages.gujarati', 'Gujarati')}</option>
                                                <option value="Tamil">{t('common:languages.tamil', 'Tamil')}</option>
                                                <option value="Telugu">{t('common:languages.telugu', 'Telugu')}</option>
                                                <option value="Kannada">{t('common:languages.kannada', 'Kannada')}</option>
                                                <option value="Malayalam">{t('common:languages.malayalam', 'Malayalam')}</option>
                                                <option value="Urdu">{t('common:languages.urdu', 'Urdu')}</option>
                                            </select>
                                        </div>

                                        <button type="submit" className="eb-btn-generate" disabled={loading.generating}>
                                            {loading.generating ? t('exam_booster:exam_booster.initializingBtn', "Initializing AI...") : t('exam_booster:exam_booster.generateBtn', "Generate Exam Ready Paper")}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {showBuyModal && (
                <div className="eb-buy-modal-overlay">
                    <div className="eb-buy-modal-card">
                        <button className="eb-close-modal" onClick={() => setShowBuyModal(false)}>×</button>
                        <div className="eb-modal-icon">🚀</div>
                        <h2>{t('exam_booster:exam_booster.buyModal.title', 'Unlock More Boosters')}</h2>
                        <p>{t('exam_booster:exam_booster.buyModal.description', "You've used your free practice sets. Get 5 more high-quality AI Exam Booster sets for just Rs. 19!")}</p>
                        
                        <div className="eb-benefit-list">
                            <div className="eb-benefit-item">✓ {t('exam_booster:exam_booster.buyModal.benefits.0', '5 Professional Practice Sets')}</div>
                            <div className="eb-benefit-item">✓ {t('exam_booster:exam_booster.buyModal.benefits.1', 'Specialized Subject Distribution')}</div>
                            <div className="eb-benefit-item">✓ {t('exam_booster:exam_booster.buyModal.benefits.2', 'Expert Explanations for All MCQ')}</div>
                        </div>

                        <button
                            className="eb-pay-now-btn"
                            onClick={handleTopUp}
                            disabled={loading.topup}
                        >
                            {loading.topup ? t('exam_booster:exam_booster.buyModal.processing', 'Processing...') : t('exam_booster:exam_booster.buyModal.payBtn', 'Pay Rs. 19 Now')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExamBoosterGenerator;
